import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, SkipForward, CheckCircle2, Sparkles, ArrowLeft,
  AlertTriangle, Wand2, PlayCircle, Lock,
} from "lucide-react";
import { KPI_TEMPLATES, extractVars } from "@/lib/kpiFormula";

const emptyConfig = {
  description: "", data_source: "", capture_example: "",
  formula_template: "", formula_human: "", output_unit: "percentage", output_decimals: 2,
  input_fields: [],
};

export default function OpsKpiWizardConfig() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [totalOriginal, setTotalOriginal] = useState(0);
  const [pendingConfirmationCount, setPendingConfirmationCount] = useState(0);
  const [step, setStep] = useState(0);
  const [configuredCount, setConfiguredCount] = useState(0);
  const [cfg, setCfg] = useState(emptyConfig);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testInputs, setTestInputs] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, statusRes] = await Promise.all([
        api.get("/ops/admin/kpis/pending-configuration"),
        api.get("/ops/admin/kpis/catalog-status").catch(() => null),
      ]);
      setPending(pendingRes.data.pending || []);
      setTotalOriginal(pendingRes.data.total || 0);
      setPendingConfirmationCount(statusRes?.data?.totals?.kpis_pending_confirmation || 0);
    } catch {
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = pending[step];

  useEffect(() => {
    if (!current) return;
    setCfg({
      description: current.description || "",
      data_source: current.data_source || "",
      capture_example: current.capture_example || "",
      formula_template: current.formula_template || "",
      formula_human: current.formula_human || "",
      output_unit: current.output_unit || "percentage",
      output_decimals: current.output_decimals ?? 2,
      input_fields: (current.input_fields || []).map((f) => ({ ...f })),
    });
    setTestOpen(false);
    setTestResult(null);
  }, [current]);

  const inputKeys = useMemo(() => cfg.input_fields.map((f) => f.key).filter(Boolean), [cfg.input_fields]);
  const missingVars = useMemo(() => extractVars(cfg.formula_template).filter((v) => !inputKeys.includes(v)), [cfg.formula_template, inputKeys]);

  const applyTemplate = (t) => setCfg({
    description: cfg.description || t.description,
    data_source: cfg.data_source || t.data_source,
    capture_example: cfg.capture_example || t.capture_example,
    formula_template: t.formula_template,
    formula_human: t.formula_human,
    output_unit: t.output_unit,
    output_decimals: t.output_decimals,
    input_fields: t.input_fields.map((f) => ({ ...f })),
  });

  const addField = () => setCfg((p) => ({ ...p, input_fields: [...p.input_fields, { key: "", label: "", type: "number", unit: "", required: true, min: null, max: null, help_text: "" }] }));
  const updField = (i, f, v) => setCfg((p) => ({ ...p, input_fields: p.input_fields.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)) }));
  const rmField = (i) => setCfg((p) => ({ ...p, input_fields: p.input_fields.filter((_, idx) => idx !== i) }));

  const next = () => {
    if (step + 1 >= pending.length) setStep(pending.length);
    else setStep(step + 1);
  };

  const saveAndNext = async () => {
    if (!inputKeys.length) { toast.error("Agrega al menos un campo de entrada"); return; }
    if (!cfg.formula_template.trim()) { toast.error("Escribe la fórmula"); return; }
    if (missingVars.length) { toast.error(`Variables no declaradas: ${missingVars.join(", ")}`); return; }
    setSaving(true);
    try {
      await api.put(`/ops/kpis/definitions/${current.id}`, {
        description: cfg.description,
        data_source: cfg.data_source,
        capture_example: cfg.capture_example,
        formula_enabled: true,
        formula_template: cfg.formula_template,
        formula_human: cfg.formula_human,
        input_fields: cfg.input_fields,
        output_unit: cfg.output_unit,
        output_decimals: parseInt(cfg.output_decimals) ?? 2,
      });
      setConfiguredCount((c) => c + 1);
      toast.success("KPI configurado");
      next();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(`/ops/kpis/definitions/${current.id}/test-formula`, {
        formula: cfg.formula_template,
        input_fields: cfg.input_fields,
        inputs: Object.fromEntries(Object.entries(testInputs).map(([k, v]) => [k, Number(v)])),
      });
      setTestResult({ ok: true, value: res.data.calculated_value });
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || "Error" });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Cargando KPIs pendientes…</div>;
  }

  const done = !current || step >= pending.length;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-5">
      <button onClick={() => navigate("/ops/admin/kpis/catalogo")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="wizard-config-back">
        <ArrowLeft className="w-4 h-4" /> Volver al catálogo
      </button>

      <div className="flex items-center gap-2">
        <Wand2 className="w-6 h-6 text-[#1A1A1A]" />
        <h1 className="text-2xl font-bold text-slate-900">Configurar fórmulas pendientes</h1>
      </div>

      {pending.length === 0 ? (
        <div className="text-center py-16" data-testid="wizard-config-empty">
          <CheckCircle2 className="w-14 h-14 text-green-400 mx-auto mb-3" />
          <p className="text-lg font-semibold text-slate-700">Todos los KPIs tienen fórmula.</p>
          {pendingConfirmationCount > 0 && (
            <>
              <p className="text-sm text-slate-500 mt-1" data-testid="wizard-config-empty-confirmation-hint">
                Revisa las {pendingConfirmationCount} propuestas pendientes de confirmación en el catálogo.
              </p>
              <button
                onClick={() => navigate("/ops/admin/kpis/revisar-propuestas")}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
                data-testid="wizard-config-go-review"
              >
                Revisar propuestas ({pendingConfirmationCount})
              </button>
            </>
          )}
        </div>
      ) : done ? (
        <div className="text-center py-16" data-testid="wizard-config-finished">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <p className="text-xl font-bold text-slate-800">{configuredCount} KPIs configurados</p>
          <p className="text-sm text-slate-400 mt-1">Los demás quedaron pendientes; puedes volver al wizard más tarde.</p>
          <button onClick={() => navigate("/ops/admin/kpis/catalogo")} className="mt-5 px-5 py-2.5 rounded-xl bg-[#1A1A1A] text-white font-semibold text-sm hover:bg-black transition-colors" data-testid="wizard-config-go-catalog">
            Ir al catálogo
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#FFDF00]" style={{ width: `${(step / totalOriginal) * 100}%` }} />
            </div>
            <span className="text-xs font-semibold text-slate-500" data-testid="wizard-config-progress">{step + 1} de {totalOriginal} configurados</span>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="wizard-config-step">
            <div>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{current.category_code}</span>
              <h2 className="text-lg font-bold text-slate-900 mt-1.5" data-testid="wizard-config-kpi-name">{current.name}</h2>
            </div>

            {/* Campos fijos (bloqueados) */}
            <div className="grid grid-cols-3 gap-2 bg-slate-50 rounded-xl p-3">
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Meta</div>
                <div className="text-sm font-semibold text-slate-700">{current.target_display || current.target || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Frecuencia</div>
                <div className="text-sm font-semibold text-slate-700 capitalize">{current.frequency || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Ponderación</div>
                <div className="text-sm font-semibold text-slate-700">{current.weight_pct != null ? `${current.weight_pct}%` : "—"}</div>
              </div>
            </div>

            {/* Sugerencia automática de fórmula */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Plantillas rápidas (sugerencia)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {KPI_TEMPLATES.map((t) => (
                  <button key={t.key} onClick={() => applyTemplate(t)} className="text-left border border-slate-200 rounded-xl px-3 py-2 hover:border-[#FFDF00] hover:bg-yellow-50 transition-colors" data-testid={`wizard-config-template-${t.key}`}>
                    <div className="text-sm font-semibold text-slate-800">{t.label}</div>
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5">{t.formula_template}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Descripción</label>
              <textarea value={cfg.description} onChange={(e) => setCfg((p) => ({ ...p, description: e.target.value }))} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none" data-testid="wizard-config-desc" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Campos de entrada (inputs)</span>
                <button onClick={addField} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-black" data-testid="wizard-config-add-field"><Plus className="w-3.5 h-3.5" /> Agregar</button>
              </div>
              <div className="space-y-2">
                {cfg.input_fields.map((f, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`wizard-config-field-${i}`}>
                    <input value={f.key} onChange={(e) => updField(i, "key", e.target.value.replace(/\s+/g, "_"))} placeholder="key" className="col-span-4 border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                    <input value={f.label} onChange={(e) => updField(i, "label", e.target.value)} placeholder="Etiqueta" className="col-span-5 border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                    <input value={f.unit || ""} onChange={(e) => updField(i, "unit", e.target.value || null)} placeholder="unidad" className="col-span-2 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                    <button onClick={() => rmField(i)} className="col-span-1 p-1.5 rounded-lg text-red-400 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {cfg.input_fields.length === 0 && <p className="text-xs text-slate-400">Aún no hay campos. Usa una plantilla o agrégalos.</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Fórmula</label>
              <input value={cfg.formula_template} onChange={(e) => setCfg((p) => ({ ...p, formula_template: e.target.value }))} placeholder="(a / b) * 100" className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00] ${missingVars.length ? "border-red-300" : "border-slate-200"}`} data-testid="wizard-config-formula" />
              {missingVars.length > 0 && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Variables no declaradas: {missingVars.join(", ")}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Fórmula legible</label>
              <input value={cfg.formula_human} onChange={(e) => setCfg((p) => ({ ...p, formula_human: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="wizard-config-formula-human" />
            </div>

            <button
              onClick={() => { setTestInputs({}); setTestResult(null); setTestOpen(true); }}
              disabled={!inputKeys.length || !cfg.formula_template.trim()}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-40"
              data-testid="wizard-config-test-btn"
            >
              <PlayCircle className="w-4 h-4" /> Probar
            </button>

            {testOpen && (
              <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50">
                {cfg.input_fields.map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label || f.key}</label>
                    <input type="number" value={testInputs[f.key] ?? ""} onChange={(e) => setTestInputs((p) => ({ ...p, [f.key]: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid={`wizard-config-test-input-${f.key}`} />
                  </div>
                ))}
                <button onClick={runTest} disabled={testing} className="w-full py-2 rounded-lg bg-[#FFDF00] font-semibold text-sm text-slate-900 hover:bg-yellow-300 transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="wizard-config-run-test-btn">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Calcular
                </button>
                {testResult && (
                  <div className={`rounded-lg px-3 py-2 text-sm ${testResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`} data-testid="wizard-config-test-result">
                    {testResult.ok ? `Resultado: ${testResult.value}` : testResult.error}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <button onClick={next} className="flex-1 inline-flex items-center justify-center gap-1.5 py-3 rounded-xl border border-slate-200 font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="wizard-config-skip">
                <SkipForward className="w-4 h-4" /> Saltar por ahora
              </button>
              <button onClick={saveAndNext} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1A1A1A] text-white font-semibold hover:bg-black transition-colors disabled:opacity-50" data-testid="wizard-config-save-next">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Guardar y siguiente
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
